import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/use-toast";
import { useLanguage } from "../../contexts/LanguageContext";

export const isCompanyRelatedPath = (path: string): boolean => {
  return (
    path.startsWith("/companies") ||
    path.startsWith("/company/") ||
    path.match(/^\/company\/\d+\/edit/) !== null ||
    path.match(/^\/company\/create/) !== null
  );
};

export const useSidebar = (defaultOpen = true) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, claims, claimsLoading } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  const isItemActive = (item: { href: string }) => {
    if (item.href === "/companies" && (claims?.role === "assimagra" || claims?.role === "get2c")) {
      return isCompanyRelatedPath(location.pathname);
    }
    if (item.href === "/" && claims?.role === "company" && location.pathname.startsWith("/companies/")) {
      return true;
    }
    return location.pathname === item.href;
  };

  const handleNavigation = (href: string) => {
    if (claimsLoading) {
      // If claims are still being loaded, show a toast to inform the user
      toast({
        title: "Please wait",
        description: "Authentication is being verified...",
        variant: "default",
      });
      return;
    }
    navigate(href);
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: t("auth.logout.success"),
        description: t("auth.logout.successDescription"),
        variant: "default",
      });
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      toast({
        title: t("auth.logout.error"),
        description: t("auth.logout.errorDescription"),
        variant: "destructive",
      });
    }
  };

  return {
    isItemActive,
    handleLogout,
    handleNavigation,
    claims,
    claimsLoading,
  };
};
